import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import DonutChart from "../../components/charts/donutChart";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import AnalyticsDatePickerModal from "../../components/modals/analyticsDatePicker/analyticsDatePickerModal";
import NoAnalyticsData from "../../components/noData/noAnalyticsData";
import { fetchAnalyticsDataFromApi } from "../../redux/slice/analyticsDataSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { CalendarIcon } from "../../svg/calendar-icon";
import { logDataToConsole } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import AnalyticCard from "./components/analyticCard";
import DrugCard from "./components/drugCard";

// Custom Donut Chart Component

interface AnalyticCardData {
  header: string;
  value: number;
  percentage: number;
}

interface DateRange {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

interface DrugData {
  drugName: string;
  drugCount: number;
  drugPercentage: number;
}

interface ColorData {
  drugName: string;
  color: string;
}

const Analytics = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: undefined,
    endDate: undefined,
  });

  const [analyticCardsData, setAnalyticCardsData] = useState<
    AnalyticCardData[]
  >([]);
  const [primaryInsuranceData, setPrimaryInsuranceData] = useState<DrugData[]>(
    [],
  );
  const [regimenData, setRegimenData] = useState<DrugData[]>([]);
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [regimenColorData, setRegimenColorData] = useState<ColorData[]>([]);
  const [insuranceColorData, setInsuranceColorData] = useState<ColorData[]>([]);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [otherRegimenData, setOtherRegimenData] = useState<number>(0);
  const [otherPrimaryInsuranceData, setOtherPrimaryInsuranceData] =
    useState<number>(0);
  const [isDefinitionLoading, setIsDefinitionLoading] = useState(false);
  const [definitionData, setDefinitionData] = useState<
    | {
        value?: string;
        definition?: string;
      }[]
    | null
  >(null);

  const dispatch = useDispatch<AppDispatch>();
  const { analyticsData, isLoading, error } = useSelector(
    (state: RootState) => state.analyticsData,
  );

  const getDefinitions = async () => {
    const orgId = getOrgIdForFetchExternalWorklist();
    if (!orgId) {
      return;
    }
    setIsDefinitionLoading(true);
    try {
      const response = await FirestoreService.getDocument(
        `/healthcare_facility/${orgId}/options_definition/bo_options`,
      );
      const data = response?.["data"];
      setDefinitionData(data);
    } catch (error) {
    } finally {
      setIsDefinitionLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchAnalyticsDataFromApi());
    getDefinitions();

    if (error) {
      logDataToConsole(error);
    }
  }, []);

  // Process analytics data from Redux store
  useEffect(() => {
    if (analyticsData?.data) {
      const result = analyticsData;

      if (result?.data?.total_orders) {
        setTotalOrders(result.data.total_orders);
      } else {
        setTotalOrders(0);
      }

      if (result?.data?.distributions?.bo_value) {
        const transformedData = result.data.distributions.bo_value.map(
          (item) => ({
            header: item.category,
            value: item.count,
            percentage: item.percentage,
          }),
        );
        setAnalyticCardsData(transformedData);
      }

      if (result?.data?.distributions?.primary_insurance) {
        const transformedData = result.data.distributions.primary_insurance.map(
          (item) => ({
            drugName: item.category,
            drugCount: item.count,
            drugPercentage: item.percentage,
          }),
        );
        setPrimaryInsuranceData(transformedData);
      }

      if (result?.data?.distributions?.regimen) {
        const transformedData = result.data.distributions.regimen.map(
          (item) => ({
            drugName: item.category,
            drugCount: item.count,
            drugPercentage: item.percentage,
          }),
        );
        setRegimenData(transformedData);
      }

      if (
        dateRange.startDate === undefined &&
        dateRange.endDate === undefined &&
        result?.data?.date_of_work_range
      ) {
        setDateRange({
          startDate: result.data.date_of_work_range.oldest_date,
          endDate: result.data.date_of_work_range.latest_date,
        });
      }
    }
  }, [analyticsData]);

  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    setDateRange({ startDate, endDate });
    dispatch(fetchAnalyticsDataFromApi(startDate, endDate));
    setShowDatePickerModal(false);
  };

  const getColorForDrug = (drugName: string, colorData: ColorData[]) => {
    const colorItem = colorData.find((item) => item.drugName === drugName);
    return colorItem ? colorItem.color : "#10B981"; // Default green if not found
  };

  useEffect(() => {
    const regimenCount = regimenData.reduce(
      (acc, item) => acc + item.drugCount,
      0,
    );
    const otherRegimenCount = totalOrders - regimenCount;

    const primaryInsuranceCount = primaryInsuranceData.reduce(
      (acc, item) => acc + item.drugCount,
      0,
    );
    const otherPrimaryInsuranceCount = totalOrders - primaryInsuranceCount;

    setOtherRegimenData(otherRegimenCount);
    setOtherPrimaryInsuranceData(otherPrimaryInsuranceCount);
  }, [regimenData, primaryInsuranceData, totalOrders]);

  useEffect(() => {
    if (otherRegimenData > 0) {
      setRegimenData([
        ...regimenData,
        {
          drugName: "Other",
          drugCount: otherRegimenData,
          drugPercentage:
            Math.round((otherRegimenData / totalOrders) * 100 * 100) / 100,
        },
      ]);
    }
  }, [otherRegimenData]);

  useEffect(() => {
    if (otherPrimaryInsuranceData > 0) {
      setPrimaryInsuranceData([
        ...primaryInsuranceData,
        {
          drugName: "Other",
          drugCount: otherPrimaryInsuranceData,
          drugPercentage:
            Math.round((otherPrimaryInsuranceData / totalOrders) * 100 * 100) /
            100,
        },
      ]);
    }
  }, [otherPrimaryInsuranceData]);

  useEffect(() => {
    if (showDatePickerModal) {
      openModal("analytics-date-picker-modal");
    }
  }, [showDatePickerModal]);

  const getDefinition = (value: string) => {
    if (definitionData) {
      const definition = definitionData.find(
        (item: any) => item.value === value,
      );
      return definition?.definition;
    }
    return null;
  };

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-primaryGray-16 px-3 pb-3">
      {isLoading || isDefinitionLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoaderMessage message="Loading analytics data..." />
        </div>
      ) : (
        <>
          <div className="analytics-header sticky top-0 z-50 flex h-12 flex-row items-center justify-between bg-primaryGray-16 p-2">
            <div className="font-bold">Analytics</div>
            <div className="mt-1 flex cursor-pointer flex-row items-center gap-2 px-2">
              <div
                onClick={() => {
                  setShowDatePickerModal(true);
                }}
                className="flex flex-row items-center gap-2 rounded-md border-2 border-primaryGray-15 bg-white p-2"
              >
                <div className="text-primaryGray-60 text-sm font-medium">
                  {moment(dateRange.startDate).format("MM/DD/YYYY")} -{" "}
                  {moment(dateRange.endDate).format("MM/DD/YYYY")}
                </div>
                <CalendarIcon />
                {/* <DateRangeIco /> */}
              </div>
            </div>
          </div>
          {error || totalOrders === 0 ? (
            <div className="flex h-full w-full items-center justify-center">
              <NoAnalyticsData
                startDate={moment(
                  analyticsData?.data?.date_of_work_range?.oldest_date,
                ).format("MM/DD/YYYY")}
                endDate={moment(
                  analyticsData?.data?.date_of_work_range?.latest_date,
                ).format("MM/DD/YYYY")}
              />
            </div>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-4 gap-4 rounded-md bg-white p-3">
                <AnalyticCard
                  header="Total Orders"
                  value={totalOrders}
                  percentage={undefined}
                />
                {analyticCardsData.map((item, index) => (
                  <AnalyticCard
                    key={index}
                    header={item.header}
                    value={item.value}
                    percentage={item.percentage}
                    showInfoIcon={true}
                    infoIconTitle={getDefinition(item.header) || ""}
                  />
                ))}
              </div>

              <div className="my-4 p-2 text-lg font-semibold">
                Regimen Split
              </div>

              <div className="w-full p-2">
                <div className="flex w-full flex-row items-center justify-around gap-4 rounded-lg border-2 border-primaryGray-15 bg-white p-3">
                  {/* Regimen Donut Chart */}
                  <div className="flex-shrink-0 p-3">
                    <DonutChart
                      data={regimenData}
                      size={240}
                      onColorData={setRegimenColorData}
                    />
                  </div>

                  {/* Drug Cards Grid */}
                  <div className="ml-36 grid w-full grid-cols-2 gap-4 p-3">
                    {regimenData.map((drug, index) => (
                      <DrugCard
                        key={index}
                        drugName={drug.drugName}
                        drugCount={drug.drugCount}
                        drugPercentage={drug.drugPercentage}
                        color={getColorForDrug(drug.drugName, regimenColorData)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="my-4 p-2 text-lg font-semibold">
                Insurance Split
              </div>

              <div className="w-full p-2">
                <div className="flex w-full flex-row items-center justify-around gap-4 rounded-lg border-2 border-primaryGray-15 bg-white py-3">
                  {/* Insurance Donut Chart */}
                  <div className="flex-shrink-0 p-3">
                    <DonutChart
                      data={primaryInsuranceData}
                      size={240}
                      onColorData={setInsuranceColorData}
                    />
                  </div>

                  {/* Drug Cards Grid */}
                  <div className="ml-36 grid w-full grid-cols-2 gap-4 p-3">
                    {primaryInsuranceData.map((drug, index) => (
                      <DrugCard
                        key={index}
                        drugName={drug.drugName}
                        drugCount={drug.drugCount}
                        drugPercentage={drug.drugPercentage}
                        color={getColorForDrug(
                          drug.drugName,
                          insuranceColorData,
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {showDatePickerModal && (
        <AnalyticsDatePickerModal
          onClose={() => setShowDatePickerModal(false)}
          onDateRangeSelect={handleDateRangeSelect}
          startDate={dateRange?.startDate}
          endDate={dateRange?.endDate}
        />
      )}
    </div>
  );
};

export default Analytics;
